import { useAppearance } from '@/shared/appearance/AppearanceProvider'
import { useI18n } from '@/shared/i18n/I18nProvider'

import { BrandEmblem } from './BrandEmblem'
import { SmartomicaLogo } from './SmartomicaLogo'
import { BRAND_BY_THEME } from './brand'

/**
 * The identity the product signs itself with: mark and name together, wherever
 * the interface introduces itself — the sign-in masthead and the header line
 * that is also the way back to the dashboard.
 *
 * IT IS ONE COMPONENT BECAUSE IT IS ONE FACT. The two surfaces used to build
 * the pair themselves, which was fine while there was one brand and is exactly
 * the kind of duplication a white-label breaks: a second brand added at one of
 * them and forgotten at the other ships a product that changes its name on
 * signing in. `brand.ts` answers whose product this is, once, and both
 * surfaces render the answer.
 *
 * THE HOST'S FORM IS A DOT DIAMOND BESIDE TRANSLATED TEXT; THE VENDOR'S IS ONE
 * DRAWING. That asymmetry is the two brands' own and not a shortcut: Terysling's
 * name is set in the interface's own type, so it follows the reading scale and
 * the theme's accent like any other word, while Smartomica's wordmark is
 * artwork with fixed letterforms that `smartomica_brand_guide.docx` §1.7
 * forbids re-setting — so this product draws the guide's own lockup rather than
 * typesetting the name beside the mark (`SmartomicaLogo` carries the
 * provenance). Both carry the name to assistive technology — the host as its
 * visible word, the vendor as the hidden one beside its drawing — so a screen
 * reader hears the same identity either way.
 *
 * The two sizes are the two places, not a scale: `sm` is the header band's
 * line and `lg` is the heading of the entry card — sign-in, the required
 * password change and the organization choice, which are one frame wearing
 * three questions. Anything else that wants to print the identity is a third
 * place, and should say so here. The vendor's
 * guide also asks each of them for a guard band of half the sign's height on
 * every side, which is why the sizes are capped against the placing surface's
 * own padding in `branding.css` rather than being plain rem numbers.
 */
export function BrandLockup({ size }: { size: 'sm' | 'lg' }) {
  const { t } = useI18n()
  const brand = BRAND_BY_THEME[useAppearance().appearance.theme]
  return (
    <span className={`brand-lockup brand-lockup--${size}`}>
      {brand === 'smartomica' ? (
        <SmartomicaLogo />
      ) : (
        <>
          <BrandEmblem className="brand-lockup__emblem" />
          <span className="brand-lockup__wordmark">{t('app.name')}</span>
        </>
      )}
    </span>
  )
}
